import React from 'react';
import { Button } from '@/components/ui/button';
import { X, User, Bed, DoorOpen, Building, IndianRupee, Phone, Mail } from 'lucide-react';

export default function BedDrawer({ isOpen, onClose, bedData, onTransfer, onVacate }) {
  if (!isOpen || !bedData) return null;

  const { bed, room, floor, tenant } = bedData;
  const isOccupied = bed.status === 'occupied';

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-slate-900/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900 font-heading">Bed Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="close-drawer"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Bed Info */}
          <div className={`p-4 rounded-xl border-2 ${
            isOccupied ? 'bg-blue-50 border-blue-300' : 'bg-green-50 border-green-300'
          }`}>
            <div className="flex items-center gap-3 mb-3">
              <Bed className={`w-8 h-8 ${isOccupied ? 'text-blue-600' : 'text-green-600'}`} />
              <div>
                <h3 className="font-bold text-slate-900">Bed {bed.bedNumber}</h3>
                <p className={`text-sm font-semibold ${
                  isOccupied ? 'text-blue-700' : 'text-green-700'
                }`}>
                  {isOccupied ? 'Occupied' : 'Vacant'}
                </p>
              </div>
            </div>

            <div className="space-y-2 text-sm text-slate-700">
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-slate-500" />
                <span>{floor.floorName}</span>
              </div>
              <div className="flex items-center gap-2">
                <DoorOpen className="w-4 h-4 text-slate-500" />
                <span>Room {room.roomNumber}</span>
              </div>
            </div>
          </div>

          {/* Tenant Info */}
          {isOccupied && tenant && (
            <div className="border-2 border-slate-200 rounded-xl p-4 space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">{tenant.name}</h4>
                  <p className="text-sm text-slate-500">Tenant</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-700">{tenant.phone}</span>
                </div>
                {tenant.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-700">{tenant.email}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                  <span className="text-sm font-medium text-slate-600">Monthly Rent</span>
                  <span className="text-lg font-bold text-slate-900 flex items-center gap-1">
                    <IndianRupee className="w-4 h-4" />
                    {tenant.monthlyRent.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Balance</span>
                  <span className={`text-lg font-bold flex items-center gap-1 ${
                    tenant.balance >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <IndianRupee className="w-4 h-4" />
                    {Math.abs(tenant.balance).toLocaleString('en-IN')}
                    <span className="text-xs ml-1">
                      {tenant.balance >= 0 ? 'Advance' : 'Due'}
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Join Date</span>
                  <span className="text-sm font-semibold text-slate-900">
                    {new Date(tenant.joinDate).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {isOccupied && tenant ? (
              <>
                <Button
                  onClick={() => onTransfer(tenant)}
                  className="w-full h-12 rounded-xl bg-primary"
                  data-testid="transfer-tenant-button"
                >
                  Transfer to Another Bed
                </Button>
                <Button
                  onClick={() => onVacate(tenant)}
                  variant="outline"
                  className="w-full h-12 rounded-xl border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="vacate-tenant-button"
                >
                  Vacate Tenant
                </Button>
              </>
            ) : (
              <Button
                className="w-full h-12 rounded-xl bg-green-600 hover:bg-green-700"
                data-testid="assign-tenant-button"
              >
                Assign New Tenant
              </Button>
            )}
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              {isOccupied
                ? 'Use "Transfer to Another Bed" and then tap a vacant bed to move this tenant on mobile. Drag and drop remains available on laptop.'
                : 'This bed is available for new tenant assignment'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
